/**
 * @file Modal de recorte cuadrado (pan + zoom) y salida JPEG para mantener el backup liviano.
 * @module imageSquareCrop
 */

/**
 * Abre el diálogo modal; resuelve a JPEG `data:` o `null` si cancela.
 * @param {string} dataUrl Imagen de entrada (`data:`…).
 * @param {{
 *   title: string,
 *   zoom: string,
 *   hint: string,
 *   apply: string,
 *   cancel: string
 * }} labels Textos UI (locale del formulario).
 * @returns {Promise<string | null>}
 */
export function openSquareImageCrop(dataUrl, labels) {
	return new Promise((resolve) => {
		const VIEW = 320;
		/** ~560px; JPEG para no inflar `localStorage`. */
		const OUT = 560;

		const backdrop = document.createElement("div");
		backdrop.className = "image-crop-backdrop";
		backdrop.setAttribute("role", "dialog");
		backdrop.setAttribute("aria-modal", "true");
		backdrop.setAttribute("aria-label", labels.title);

		const panel = document.createElement("div");
		panel.className = "image-crop-panel";

		const title = document.createElement("h2");
		title.className = "image-crop-title";
		title.textContent = labels.title;

		const hint = document.createElement("p");
		hint.className = "image-crop-hint";
		hint.textContent = labels.hint;

		const viewport = document.createElement("div");
		viewport.className = "image-crop-viewport";
		viewport.style.width = `${VIEW}px`;
		viewport.style.height = `${VIEW}px`;

		const img = document.createElement("img");
		img.className = "image-crop-img";
		img.alt = "";
		img.draggable = false;

		const zoomRow = document.createElement("div");
		zoomRow.className = "image-crop-zoom";
		const zoomLabel = document.createElement("label");
		zoomLabel.className = "image-crop-zoom-label";
		zoomLabel.textContent = labels.zoom;
		const zoomInput = document.createElement("input");
		zoomInput.type = "range";
		zoomInput.min = "1";
		zoomInput.max = "3";
		zoomInput.step = "0.05";
		zoomInput.value = "1";
		zoomLabel.append(zoomInput);

		const actions = document.createElement("div");
		actions.className = "image-crop-actions";
		const btnCancel = document.createElement("button");
		btnCancel.type = "button";
		btnCancel.className = "app-button app-button--secondary";
		btnCancel.textContent = labels.cancel;
		const btnApply = document.createElement("button");
		btnApply.type = "button";
		btnApply.className = "app-button app-button--primary";
		btnApply.textContent = labels.apply;

		viewport.append(img);
		zoomRow.append(zoomLabel);
		actions.append(btnCancel, btnApply);
		panel.append(title, hint, viewport, zoomRow, actions);
		backdrop.append(panel);
		document.body.append(backdrop);

		let nw = 0;
		let nh = 0;
		/** display scale: CSS px per natural px */
		let ds = 1;
		let tx = 0;
		let ty = 0;
		let zoom = 1;

		function baseScale() {
			return Math.max(VIEW / nw, VIEW / nh);
		}

		function applyTransform() {
			ds = baseScale() * zoom;
			img.style.width = `${nw * ds}px`;
			img.style.height = `${nh * ds}px`;
			img.style.left = `${tx}px`;
			img.style.top = `${ty}px`;
		}

		function clampPan() {
			const minTx = VIEW - nw * ds;
			const minTy = VIEW - nh * ds;
			tx = Math.min(0, Math.max(minTx, tx));
			ty = Math.min(0, Math.max(minTy, ty));
		}

		function syncFromZoom() {
			zoom = Number(zoomInput.value);
			const prevDs = ds;
			ds = baseScale() * zoom;
			if (prevDs > 0 && nw && nh) {
				const cx = VIEW / 2;
				const cy = VIEW / 2;
				const nx = (cx - tx) / prevDs;
				const ny = (cy - ty) / prevDs;
				tx = cx - nx * ds;
				ty = cy - ny * ds;
			}
			clampPan();
			applyTransform();
		}

		img.onload = () => {
			nw = img.naturalWidth;
			nh = img.naturalHeight;
			if (!nw || !nh) {
				cleanup();
				resolve(null);
				return;
			}
			zoom = 1;
			zoomInput.value = "1";
			ds = baseScale() * zoom;
			tx = (VIEW - nw * ds) / 2;
			ty = (VIEW - nh * ds) / 2;
			clampPan();
			applyTransform();
			btnApply.focus();
		};
		img.onerror = () => {
			cleanup();
			resolve(null);
		};
		img.src = dataUrl;

		zoomInput.addEventListener("input", syncFromZoom);

		let drag = null;
		viewport.addEventListener("pointerdown", (e) => {
			if (e.button !== 0) return;
			drag = { x: e.clientX, y: e.clientY, tx, ty };
			viewport.setPointerCapture(e.pointerId);
		});
		viewport.addEventListener("pointermove", (e) => {
			if (!drag) return;
			tx = drag.tx + (e.clientX - drag.x);
			ty = drag.ty + (e.clientY - drag.y);
			clampPan();
			img.style.left = `${tx}px`;
			img.style.top = `${ty}px`;
		});
		viewport.addEventListener("pointerup", () => {
			drag = null;
		});
		viewport.addEventListener("pointercancel", () => {
			drag = null;
		});

		function cleanup() {
			backdrop.remove();
			document.removeEventListener("keydown", onKey);
		}

		function finish(data) {
			cleanup();
			resolve(data);
		}

		function onKey(e) {
			if (e.key === "Escape") {
				e.preventDefault();
				finish(null);
			}
		}
		document.addEventListener("keydown", onKey);

		btnCancel.addEventListener("click", () => finish(null));
		btnApply.addEventListener("click", () => {
			if (!nw || !nh) return;
			const sx = (0 - tx) / ds;
			const sy = (0 - ty) / ds;
			const sw = VIEW / ds;
			const sh = VIEW / ds;
			const canvas = document.createElement("canvas");
			canvas.width = OUT;
			canvas.height = OUT;
			const ctx = canvas.getContext("2d");
			if (!ctx) {
				finish(null);
				return;
			}
			ctx.drawImage(img, sx, sy, sw, sh, 0, 0, OUT, OUT);
			finish(canvas.toDataURL("image/jpeg", 0.92));
		});

		backdrop.addEventListener("click", (e) => {
			if (e.target === backdrop) finish(null);
		});
	});
}
